/**
 * Thread-scoped right-panel surface state.
 *
 * This is the compatibility adapter between feature-specific panel actions and
 * the declarative Workspace Surface Host reducer. Each feature continues to own
 * its durable resource state. Browser surfaces point at preview tab ids,
 * terminal surfaces point at terminal session ids, file surfaces point at
 * workspace paths, native subagent conversations point at agent thread ids,
 * and diff/files/Tasks remain singleton surfaces. Agent Task Lists remain
 * distinct from native Codex Subagents.
 */
import { scopedThreadKey } from "@afar-research/client-runtime/environment";
import type { ScopedThreadRef } from "@afar-research/contracts";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { resolveStorage } from "./lib/storage";
import { RIGHT_PANEL_SURFACE_DEFINITIONS } from "./rightPanelSurfaceDefinitions";
import {
  dispatchWorkspaceSurfaceState,
  reportWorkspaceSurfaceFailure,
  restoreWorkspaceSurfaceDescriptors,
  selectWorkspaceSurfaceDescriptorsForPersistence,
  serializeWorkspaceSurfaceDescriptors,
  type PersistedWorkspaceSurfaceDescriptor,
  type WorkspaceSurfaceFailure,
  type WorkspaceSurfaceCommand,
  type WorkspaceSurfaceState,
} from "./workspaceSurfaceHost";

export const RIGHT_PANEL_KINDS = [
  "diff",
  "files",
  "file",
  "preview",
  "terminal",
  "experiment",
  "pull-requests",
  "pull-request",
  "contributions",
  "screen",
  "tasks",
  "agent",
  "workflow",
] as const;
export type RightPanelKind = (typeof RIGHT_PANEL_KINDS)[number];

export type RightPanelSurface =
  | { id: `browser:${string}`; kind: "preview"; resourceId: string }
  | { id: "browser:new"; kind: "preview"; resourceId: null }
  | {
      id: `terminal:${string}`;
      kind: "terminal";
      resourceId: string;
      terminalIds: string[];
      activeTerminalId: string;
      splitDirection?: "horizontal" | "vertical";
    }
  | { id: "diff"; kind: "diff" }
  | { id: "files"; kind: "files" }
  | { id: "pull-requests"; kind: "pull-requests" }
  /** Who wrote the thread checkout's code, from its git-ai Authorship Logs. */
  | { id: "contributions"; kind: "contributions" }
  /** A Cloud Workspace's Cloud Display, live (ADR-0137). */
  | { id: "screen"; kind: "screen" }
  | {
      /**
       * One pull request opened beside a thread. The target lives in the id, so several can
       * stay open as peer tabs next to the thread's linked collection.
       */
      id: `pull-request:${string}`;
      kind: "pull-request";
      projectId: string;
      /** Set where the pull request is not in the Project's own repository. */
      host?: string;
      repository: string;
      number: number;
      url: string;
    }
  | {
      id: `file:${string}`;
      kind: "file";
      relativePath: string;
      revealLine: number | null;
      revealRequestId: number;
    }
  /**
   * The Experiment Workspace of the thread's checkout: its catalog, or one Experiment Folder's
   * dashboard when `experimentId` names it.
   */
  | { id: "experiments"; kind: "experiment"; experimentId: string | null }
  | { id: "tasks"; kind: "tasks"; resourceId: string | null }
  | { id: `agent:${string}`; kind: "agent"; agentId: string }
  /** One Workflow of the thread's checkout, by its folder name (ADR 0141). */
  | { id: `workflow:${string}`; kind: "workflow"; workflowName: string };

const RIGHT_PANEL_STORAGE_KEY = "afar-research:right-panel-state:v2";

export interface ThreadRightPanelState extends WorkspaceSurfaceState<RightPanelSurface> {}

interface PersistedThreadRightPanelState {
  readonly isOpen: boolean;
  readonly activeSurfaceId: string | null;
  readonly surfaces: readonly PersistedWorkspaceSurfaceDescriptor<RightPanelSurface>[];
}

interface PersistedRightPanelStoreState {
  readonly byThreadKey: Readonly<Record<string, PersistedThreadRightPanelState>>;
}

export function selectDevicePersistedRightPanelState(
  byThreadKey: Readonly<Record<string, ThreadRightPanelState>>,
): Record<string, ThreadRightPanelState> {
  return Object.fromEntries(
    Object.entries(byThreadKey).flatMap(([threadKey, threadState]) => {
      const surfaces = selectWorkspaceSurfaceDescriptorsForPersistence(
        RIGHT_PANEL_SURFACE_DEFINITIONS,
        threadState.surfaces,
        "device",
      );
      if (surfaces.length === 0) return [];
      const activeSurfaceId = surfaces.some((surface) => surface.id === threadState.activeSurfaceId)
        ? threadState.activeSurfaceId
        : (surfaces.at(-1)?.id ?? null);
      return [
        [
          threadKey,
          {
            isOpen: threadState.isOpen,
            activeSurfaceId,
            surfaces: [...surfaces],
          },
        ],
      ];
    }),
  );
}

export function serializeDevicePersistedRightPanelState(
  byThreadKey: Readonly<Record<string, ThreadRightPanelState>>,
): Readonly<Record<string, PersistedThreadRightPanelState>> {
  return Object.fromEntries(
    Object.entries(selectDevicePersistedRightPanelState(byThreadKey)).map(
      ([threadKey, threadState]) => [
        threadKey,
        {
          ...threadState,
          surfaces: serializeWorkspaceSurfaceDescriptors(
            RIGHT_PANEL_SURFACE_DEFINITIONS,
            threadState.surfaces,
            "device",
            reportWorkspaceSurfaceFailure,
          ),
        },
      ],
    ),
  );
}

export function restoreDevicePersistedRightPanelStoreState(
  persistedState: unknown,
  onFailure: (failure: WorkspaceSurfaceFailure) => void = reportWorkspaceSurfaceFailure,
): Pick<RightPanelStoreState, "byThreadKey"> {
  if (!persistedState || typeof persistedState !== "object") {
    return { byThreadKey: {} };
  }
  const persisted = persistedState as Readonly<Record<string, unknown>>;
  const rawByThreadKey =
    persisted.byThreadKey && typeof persisted.byThreadKey === "object"
      ? (persisted.byThreadKey as Readonly<Record<string, unknown>>)
      : {};
  const byThreadKey = Object.fromEntries(
    Object.entries(rawByThreadKey).flatMap(([threadKey, value]) => {
      if (!value || typeof value !== "object") return [];
      const threadState = value as Readonly<Record<string, unknown>>;
      const surfaces = restoreWorkspaceSurfaceDescriptors(
        RIGHT_PANEL_SURFACE_DEFINITIONS,
        Array.isArray(threadState.surfaces)
          ? (threadState.surfaces as readonly PersistedWorkspaceSurfaceDescriptor[])
          : [],
        "device",
        onFailure,
      );
      if (surfaces.length === 0) return [];
      const requestedActiveSurfaceId =
        typeof threadState.activeSurfaceId === "string" ? threadState.activeSurfaceId : null;
      const activeSurfaceId = surfaces.some((surface) => surface.id === requestedActiveSurfaceId)
        ? requestedActiveSurfaceId
        : (surfaces.at(-1)?.id ?? null);
      return [
        [
          threadKey,
          {
            isOpen: threadState.isOpen === true,
            activeSurfaceId,
            surfaces: [...surfaces],
          },
        ],
      ];
    }),
  );
  return { byThreadKey };
}

function serializePersistedRightPanelStoreState(input: {
  readonly byThreadKey: Readonly<Record<string, ThreadRightPanelState>>;
}): PersistedRightPanelStoreState {
  return { byThreadKey: serializeDevicePersistedRightPanelState(input.byThreadKey) };
}

interface RightPanelStoreState {
  byThreadKey: Record<string, ThreadRightPanelState>;
  open: (
    ref: ScopedThreadRef,
    kind: Exclude<
      RightPanelKind,
      "file" | "terminal" | "agent" | "pull-request" | "workflow" | "experiment"
    >,
  ) => void;
  openTasks: (ref: ScopedThreadRef, taskListInstanceId: string) => void;
  openAgent: (ref: ScopedThreadRef, agentId: string) => void;
  openBrowser: (ref: ScopedThreadRef, tabId: string | null) => void;
  openFile: (ref: ScopedThreadRef, relativePath: string, line?: number) => void;
  openPullRequest: (ref: ScopedThreadRef, target: RightPanelPullRequestTarget) => void;
  openWorkflow: (ref: ScopedThreadRef, workflowName: string) => void;
  /** Opens the thread's Experiment tab on its catalog, or on one Experiment Folder. */
  openExperiment: (ref: ScopedThreadRef, experimentId: string | null) => void;
  openTerminal: (ref: ScopedThreadRef, terminalId: string) => void;
  splitTerminal: (
    ref: ScopedThreadRef,
    surfaceId: string,
    terminalId: string,
    direction?: "horizontal" | "vertical",
  ) => void;
  activateTerminal: (ref: ScopedThreadRef, surfaceId: string, terminalId: string) => void;
  closeTerminal: (ref: ScopedThreadRef, surfaceId: string, terminalId: string) => void;
  activateSurface: (ref: ScopedThreadRef, surfaceId: string) => void;
  dispatchSurfaceCommand: (
    ref: ScopedThreadRef,
    command: WorkspaceSurfaceCommand<RightPanelSurface>,
  ) => void;
  closeSurface: (ref: ScopedThreadRef, surfaceId: string) => void;
  closeOtherSurfaces: (ref: ScopedThreadRef, surfaceId: string) => void;
  closeSurfacesToRight: (ref: ScopedThreadRef, surfaceId: string) => void;
  closeAllSurfaces: (ref: ScopedThreadRef) => void;
  reconcileBrowserSurfaces: (ref: ScopedThreadRef, tabIds: readonly string[]) => void;
  reconcileFileSurfaces: (ref: ScopedThreadRef, workspaceAvailable: boolean) => void;
  reconcileTasksSurface: (ref: ScopedThreadRef, available: boolean) => void;
  reconcileAgentSurfaces: (ref: ScopedThreadRef, agentIds: readonly string[]) => void;
  show: (ref: ScopedThreadRef) => void;
  close: (ref: ScopedThreadRef) => void;
  toggleVisibility: (ref: ScopedThreadRef) => void;
  toggle: (
    ref: ScopedThreadRef,
    kind: Exclude<
      RightPanelKind,
      "file" | "terminal" | "agent" | "pull-request" | "workflow" | "experiment"
    >,
  ) => void;
  removeThread: (ref: ScopedThreadRef) => void;
}

const EMPTY_THREAD_STATE: ThreadRightPanelState = {
  isOpen: false,
  activeSurfaceId: null,
  surfaces: [],
};

const singletonSurface = (
  kind: Exclude<
    RightPanelKind,
    "file" | "preview" | "terminal" | "agent" | "pull-request" | "workflow" | "experiment"
  >,
): RightPanelSurface => {
  switch (kind) {
    case "diff":
      return { id: "diff", kind };
    case "files":
      return { id: "files", kind };
    case "pull-requests":
      return { id: "pull-requests", kind };
    case "contributions":
      return { id: "contributions", kind };
    case "screen":
      return { id: "screen", kind };
    case "tasks":
      return { id: "tasks", kind, resourceId: null };
  }
};

const browserSurface = (tabId: string | null): RightPanelSurface =>
  tabId
    ? { id: `browser:${tabId}`, kind: "preview", resourceId: tabId }
    : { id: "browser:new", kind: "preview", resourceId: null };

export type RightPanelPullRequestTarget = {
  readonly projectId: string;
  readonly host?: string;
  readonly repository: string;
  readonly number: number;
  readonly url: string;
};

export function rightPanelPullRequestSurfaceId(
  target: Omit<RightPanelPullRequestTarget, "url">,
): `pull-request:${string}` {
  // The host is part of the id: the same repository name on two hosts is two pull requests.
  const host = target.host === undefined ? "" : `${encodeURIComponent(target.host.toLowerCase())}:`;
  return `pull-request:${encodeURIComponent(target.projectId)}:${host}${encodeURIComponent(target.repository)}:${target.number}`;
}

const pullRequestSurface = (target: RightPanelPullRequestTarget): RightPanelSurface => ({
  id: rightPanelPullRequestSurfaceId(target),
  kind: "pull-request",
  projectId: target.projectId,
  ...(typeof target.host === "string" ? { host: target.host.toLowerCase() } : {}),
  repository: target.repository,
  number: target.number,
  url: target.url,
});

export function rightPanelWorkflowSurfaceId(workflowName: string): `workflow:${string}` {
  return `workflow:${workflowName}`;
}

const workflowSurface = (workflowName: string): RightPanelSurface => ({
  id: rightPanelWorkflowSurfaceId(workflowName),
  kind: "workflow",
  workflowName,
});

const experimentSurface = (experimentId: string | null): RightPanelSurface => ({
  id: "experiments",
  kind: "experiment",
  experimentId,
});

const fileSurface = (
  relativePath: string,
  revealLine: number | null,
  revealRequestId: number,
): RightPanelSurface => ({
  id: `file:${relativePath}`,
  kind: "file",
  relativePath,
  revealLine,
  revealRequestId,
});

const terminalSurface = (terminalId: string): RightPanelSurface => ({
  id: `terminal:${terminalId}`,
  kind: "terminal",
  resourceId: terminalId,
  terminalIds: [terminalId],
  activeTerminalId: terminalId,
});

const agentSurface = (agentId: string): RightPanelSurface => ({
  id: `agent:${agentId}`,
  kind: "agent",
  agentId,
});

const upsertSurface = (
  current: ThreadRightPanelState,
  surface: RightPanelSurface,
  activate = true,
): ThreadRightPanelState =>
  dispatchWorkspaceSurfaceState(current, {
    type: "open",
    descriptor: surface,
    activate,
  });

const updateThread = (
  byThreadKey: Record<string, ThreadRightPanelState>,
  threadKey: string,
  updater: (current: ThreadRightPanelState) => ThreadRightPanelState,
): Record<string, ThreadRightPanelState> => {
  const current = byThreadKey[threadKey] ?? EMPTY_THREAD_STATE;
  const next = updater(current);
  if (!next.isOpen && next.activeSurfaceId === null && next.surfaces.length === 0) {
    if (!(threadKey in byThreadKey)) return byThreadKey;
    const { [threadKey]: _removed, ...rest } = byThreadKey;
    return rest;
  }
  if (next === current) return byThreadKey;
  return { ...byThreadKey, [threadKey]: next };
};

const dispatchSurfaceCommand = (
  byThreadKey: Record<string, ThreadRightPanelState>,
  ref: ScopedThreadRef,
  command: WorkspaceSurfaceCommand<RightPanelSurface>,
): Record<string, ThreadRightPanelState> =>
  updateThread(byThreadKey, scopedThreadKey(ref), (current) =>
    dispatchWorkspaceSurfaceState(current, command),
  );

function removeSurfaces(
  current: ThreadRightPanelState,
  shouldRemove: (surface: RightPanelSurface) => boolean,
): ThreadRightPanelState {
  const surfaces = current.surfaces.filter((surface) => !shouldRemove(surface));
  if (surfaces.length === current.surfaces.length) return current;
  const activeStillExists = surfaces.some((surface) => surface.id === current.activeSurfaceId);
  return {
    ...current,
    isOpen: surfaces.length > 0 ? current.isOpen : false,
    surfaces,
    activeSurfaceId: activeStillExists ? current.activeSurfaceId : (surfaces.at(-1)?.id ?? null),
  };
}

function normalizeRevealLine(line: number | undefined): number | null {
  if (line === undefined || !Number.isFinite(line)) return null;
  return Math.max(1, Math.trunc(line));
}

export function migratePersistedRightPanelState(persistedState: unknown): {
  byThreadKey: Record<string, ThreadRightPanelState>;
} {
  if (!persistedState || typeof persistedState !== "object") {
    return { byThreadKey: {} };
  }
  const byThreadKey =
    "byThreadKey" in persistedState &&
    persistedState.byThreadKey &&
    typeof persistedState.byThreadKey === "object"
      ? Object.fromEntries(
          Object.entries(persistedState.byThreadKey as Record<string, ThreadRightPanelState>).map(
            ([threadKey, threadState]) => {
              const validThreadState =
                threadState && typeof threadState === "object" ? threadState : null;
              const surfaces = Array.isArray(validThreadState?.surfaces)
                ? validThreadState.surfaces.flatMap<RightPanelSurface>((surface) => {
                    const persistedKind = (surface as { kind?: string }).kind;
                    // Retired singleton surfaces: Design runs its operations from the
                    // composer, the Experiment tab replaced the scope-bound dashboard, and
                    // Media is a composer Action rather than a panel.
                    if (
                      persistedKind === "media-creation" ||
                      persistedKind === "experiment-signals" ||
                      persistedKind === "experiment-dashboard" ||
                      persistedKind === "design-overview" ||
                      persistedKind === "peer-evidence" ||
                      persistedKind === "plan" ||
                      persistedKind === "agents"
                    ) {
                      return [];
                    }
                    if (!RIGHT_PANEL_KINDS.some((kind) => kind === persistedKind)) return [];
                    if (surface.kind === "file") {
                      const revealLine =
                        typeof surface.revealLine === "number" &&
                        Number.isFinite(surface.revealLine)
                          ? Math.max(1, Math.trunc(surface.revealLine))
                          : null;
                      const revealRequestId =
                        typeof surface.revealRequestId === "number" &&
                        Number.isSafeInteger(surface.revealRequestId) &&
                        surface.revealRequestId >= 0
                          ? surface.revealRequestId
                          : 0;
                      return [{ ...surface, revealLine, revealRequestId }];
                    }
                    if (surface.kind === "tasks") {
                      return [
                        {
                          ...surface,
                          resourceId:
                            "resourceId" in surface && typeof surface.resourceId === "string"
                              ? surface.resourceId
                              : null,
                        },
                      ];
                    }
                    if (surface.kind !== "terminal") return [surface];
                    if (
                      !("resourceId" in surface) ||
                      typeof surface.resourceId !== "string" ||
                      surface.id !== `terminal:${surface.resourceId}`
                    ) {
                      return [];
                    }
                    const terminalIds =
                      "terminalIds" in surface && Array.isArray(surface.terminalIds)
                        ? [
                            ...new Set(
                              surface.terminalIds.filter(
                                (terminalId): terminalId is string =>
                                  typeof terminalId === "string",
                              ),
                            ),
                          ]
                        : [surface.resourceId];
                    const activeTerminalId =
                      "activeTerminalId" in surface &&
                      typeof surface.activeTerminalId === "string" &&
                      terminalIds.includes(surface.activeTerminalId)
                        ? surface.activeTerminalId
                        : (terminalIds[0] ?? surface.resourceId);
                    return [
                      {
                        ...surface,
                        terminalIds: terminalIds.length > 0 ? terminalIds : [surface.resourceId],
                        activeTerminalId,
                      },
                    ];
                  })
                : [];
              const activeSurfaceId = surfaces.some(
                (surface) => surface.id === validThreadState?.activeSurfaceId,
              )
                ? (validThreadState?.activeSurfaceId ?? null)
                : // Retired singleton ids. These are persisted browser state,
                  // not live code: without them a thread whose Brain or Design
                  // tab was active reopens on an empty panel instead of the
                  // surface it kept.
                  validThreadState?.activeSurfaceId === "brain:overview" ||
                    validThreadState?.activeSurfaceId === "brain:knowledge" ||
                    validThreadState?.activeSurfaceId === "brain:sources" ||
                    validThreadState?.activeSurfaceId === "design:overview" ||
                    validThreadState?.activeSurfaceId === "agents" ||
                    validThreadState?.activeSurfaceId === "experiment:dashboard" ||
                    validThreadState?.activeSurfaceId === "experiment:signals" ||
                    validThreadState?.activeSurfaceId === "peer:evidence" ||
                    validThreadState?.activeSurfaceId === "media:creation"
                  ? (surfaces[0]?.id ?? null)
                  : null;
              const isOpen =
                surfaces.length > 0 &&
                (typeof validThreadState?.isOpen === "boolean"
                  ? validThreadState.isOpen
                  : activeSurfaceId !== null);
              return [threadKey, { isOpen, surfaces, activeSurfaceId }];
            },
          ),
        )
      : {};
  const legacyAutoInitializedPeerThreadKeys = new Set(
    "peerInitializedThreadKeys" in persistedState &&
      Array.isArray(persistedState.peerInitializedThreadKeys)
      ? persistedState.peerInitializedThreadKeys.filter(
          (value): value is string => typeof value === "string",
        )
      : [],
  );
  const migratedByThreadKey = Object.fromEntries(
    Object.entries(byThreadKey).filter(
      ([threadKey, state]) =>
        !(
          legacyAutoInitializedPeerThreadKeys.has(threadKey) &&
          !state.isOpen &&
          state.activeSurfaceId === null &&
          state.surfaces.length === 0
        ),
    ),
  );
  return { byThreadKey: migratedByThreadKey };
}

export const useRightPanelStore = create<RightPanelStoreState>()(
  persist(
    (set) => ({
      byThreadKey: {},
      open: (ref, kind) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            if (kind === "preview") {
              const existing = current.surfaces.find((surface) => surface.kind === "preview");
              return upsertSurface(current, existing ?? browserSurface(null));
            }
            return upsertSurface(current, singletonSurface(kind));
          }),
        })),
      openTasks: (ref, taskListInstanceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            upsertSurface(current, {
              id: "tasks",
              kind: "tasks",
              resourceId: taskListInstanceId,
            }),
          ),
        })),
      openBrowser: (ref, tabId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surface = browserSurface(tabId);
            const withoutPlaceholder = tabId
              ? current.surfaces.filter((entry) => entry.id !== "browser:new")
              : current.surfaces;
            return upsertSurface({ ...current, surfaces: withoutPlaceholder }, surface);
          }),
        })),
      openAgent: (ref, agentId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            upsertSurface(current, agentSurface(agentId)),
          ),
        })),
      openPullRequest: (ref, target) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surface = pullRequestSurface(target);
            const existing = current.surfaces.some((entry) => entry.id === surface.id);
            return {
              isOpen: true,
              activeSurfaceId: surface.id,
              // Reopening the same pull request activates its tab and refreshes the locator it
              // was opened from rather than stacking a second tab for the same target.
              surfaces: existing
                ? current.surfaces.map((entry) => (entry.id === surface.id ? surface : entry))
                : [...current.surfaces, surface],
            };
          }),
        })),
      openWorkflow: (ref, workflowName) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            upsertSurface(current, workflowSurface(workflowName)),
          ),
        })),
      openExperiment: (ref, experimentId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surface = experimentSurface(experimentId);
            const existing = current.surfaces.some((entry) => entry.id === surface.id);
            return {
              isOpen: true,
              activeSurfaceId: surface.id,
              // One Experiment tab per thread: opening it again moves it to the asked folder.
              surfaces: existing
                ? current.surfaces.map((entry) => (entry.id === surface.id ? surface : entry))
                : [...current.surfaces, surface],
            };
          }),
        })),
      openFile: (ref, relativePath, line) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const withoutStandaloneExplorer = current.surfaces.filter(
              (surface) => surface.kind !== "files",
            );
            const surfaceId = `file:${relativePath}` as const;
            const existing = withoutStandaloneExplorer.find(
              (surface): surface is Extract<RightPanelSurface, { kind: "file" }> =>
                surface.id === surfaceId && surface.kind === "file",
            );
            const surface = fileSurface(
              relativePath,
              normalizeRevealLine(line),
              (existing?.revealRequestId ?? 0) + 1,
            );
            return {
              isOpen: true,
              activeSurfaceId: surface.id,
              surfaces: existing
                ? withoutStandaloneExplorer.map((entry) =>
                    entry.id === surface.id ? surface : entry,
                  )
                : [...withoutStandaloneExplorer, surface],
            };
          }),
        })),
      openTerminal: (ref, terminalId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            upsertSurface(current, terminalSurface(terminalId)),
          ),
        })),
      splitTerminal: (ref, surfaceId, terminalId, direction = "horizontal") =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => ({
            ...current,
            isOpen: true,
            activeSurfaceId: surfaceId,
            surfaces: current.surfaces.map((surface) => {
              if (surface.id !== surfaceId || surface.kind !== "terminal") return surface;
              const { splitDirection: _splitDirection, ...baseSurface } = surface;
              return {
                ...baseSurface,
                terminalIds: surface.terminalIds.includes(terminalId)
                  ? surface.terminalIds
                  : [...surface.terminalIds, terminalId],
                activeTerminalId: terminalId,
                ...(direction === "vertical" ? { splitDirection: "vertical" as const } : {}),
              };
            }),
          })),
        })),
      activateTerminal: (ref, surfaceId, terminalId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => ({
            ...current,
            activeSurfaceId: surfaceId,
            surfaces: current.surfaces.map((surface) =>
              surface.id === surfaceId &&
              surface.kind === "terminal" &&
              surface.terminalIds.includes(terminalId)
                ? { ...surface, activeTerminalId: terminalId }
                : surface,
            ),
          })),
        })),
      closeTerminal: (ref, surfaceId, terminalId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surface = current.surfaces.find(
              (entry) => entry.id === surfaceId && entry.kind === "terminal",
            );
            if (!surface || surface.kind !== "terminal") return current;
            const terminalIds = surface.terminalIds.filter((id) => id !== terminalId);
            if (terminalIds.length === 0) {
              const index = current.surfaces.findIndex((entry) => entry.id === surfaceId);
              const surfaces = current.surfaces.filter((entry) => entry.id !== surfaceId);
              const fallback = surfaces[Math.min(index, surfaces.length - 1)] ?? null;
              return {
                ...current,
                isOpen: surfaces.length > 0 && current.isOpen,
                surfaces,
                activeSurfaceId:
                  current.activeSurfaceId === surfaceId
                    ? (fallback?.id ?? null)
                    : current.activeSurfaceId,
              };
            }
            return {
              ...current,
              surfaces: current.surfaces.map((entry) =>
                entry.id === surfaceId && entry.kind === "terminal"
                  ? {
                      ...entry,
                      terminalIds,
                      activeTerminalId:
                        entry.activeTerminalId === terminalId
                          ? (terminalIds.at(-1) ?? terminalIds[0]!)
                          : entry.activeTerminalId,
                    }
                  : entry,
              ),
            };
          }),
        })),
      activateSurface: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: dispatchSurfaceCommand(state.byThreadKey, ref, {
            type: "activate",
            surfaceId,
          }),
        })),
      dispatchSurfaceCommand: (ref, command) =>
        set((state) => ({
          byThreadKey: dispatchSurfaceCommand(state.byThreadKey, ref, command),
        })),
      closeSurface: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: dispatchSurfaceCommand(state.byThreadKey, ref, {
            type: "close",
            surfaceId,
          }),
        })),
      closeOtherSurfaces: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: dispatchSurfaceCommand(state.byThreadKey, ref, {
            type: "close-others",
            surfaceId,
          }),
        })),
      closeSurfacesToRight: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: dispatchSurfaceCommand(state.byThreadKey, ref, {
            type: "close-to-right",
            surfaceId,
          }),
        })),
      closeAllSurfaces: (ref) =>
        set((state) => ({
          byThreadKey: dispatchSurfaceCommand(state.byThreadKey, ref, { type: "close-all" }),
        })),
      reconcileBrowserSurfaces: (ref, tabIds) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const validIds = new Set(tabIds.map((tabId) => `browser:${tabId}`));
            const nonBrowser = current.surfaces.filter((surface) => surface.kind !== "preview");
            const existingBrowser = current.surfaces.filter(
              (surface): surface is Extract<RightPanelSurface, { kind: "preview" }> =>
                surface.kind === "preview" &&
                surface.id !== "browser:new" &&
                validIds.has(surface.id),
            );
            const knownIds = new Set(existingBrowser.map((surface) => surface.id));
            const added = tabIds
              .filter((tabId) => !knownIds.has(`browser:${tabId}`))
              .map((tabId) => browserSurface(tabId));
            const surfaces = [...nonBrowser, ...existingBrowser, ...added];
            const activeStillExists = surfaces.some(
              (surface) => surface.id === current.activeSurfaceId,
            );
            const fallbackBrowser = surfaces.find((surface) => surface.kind === "preview");
            return {
              ...current,
              surfaces,
              activeSurfaceId: activeStillExists
                ? current.activeSurfaceId
                : (fallbackBrowser?.id ?? surfaces[0]?.id ?? null),
            };
          }),
        })),
      reconcileFileSurfaces: (ref, workspaceAvailable) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            if (workspaceAvailable) return current;
            return removeSurfaces(
              current,
              (surface) => surface.kind === "files" || surface.kind === "file",
            );
          }),
        })),
      reconcileTasksSurface: (ref, available) =>
        set((state) => {
          if (available) return state;
          const threadKey = scopedThreadKey(ref);
          const current = state.byThreadKey[threadKey];
          if (!current?.surfaces.some((surface) => surface.kind === "tasks")) return state;
          return {
            byThreadKey: updateThread(state.byThreadKey, threadKey, (threadState) =>
              removeSurfaces(threadState, (surface) => surface.kind === "tasks"),
            ),
          };
        }),
      reconcileAgentSurfaces: (ref, agentIds) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const availableAgentIds = new Set(agentIds);
            return removeSurfaces(
              current,
              (surface) =>
                (surface as { kind: string }).kind === "agents" ||
                (surface.kind === "agent" && !availableAgentIds.has(surface.agentId)),
            );
          }),
        })),
      show: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            dispatchWorkspaceSurfaceState(current, { type: "show" }),
          ),
        })),
      close: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            dispatchWorkspaceSurfaceState(current, { type: "hide" }),
          ),
        })),
      toggleVisibility: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            dispatchWorkspaceSurfaceState(current, { type: "toggle-visibility" }),
          ),
        })),
      toggle: (ref, kind) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const active = current.surfaces.find(
              (surface) => surface.id === current.activeSurfaceId,
            );
            if (current.isOpen && active?.kind === kind) {
              return { ...current, isOpen: false };
            }
            if (kind === "preview") {
              const existing = current.surfaces.find((surface) => surface.kind === "preview");
              return upsertSurface(current, existing ?? browserSurface(null));
            }
            return upsertSurface(current, singletonSurface(kind));
          }),
        })),
      removeThread: (ref) =>
        set((state) => {
          const threadKey = scopedThreadKey(ref);
          const { [threadKey]: _removed, ...rest } = state.byThreadKey;
          return { byThreadKey: rest };
        }),
    }),
    {
      name: RIGHT_PANEL_STORAGE_KEY,
      storage: createJSONStorage(() =>
        resolveStorage(typeof window !== "undefined" ? window.localStorage : undefined),
      ),
      partialize: (state) => ({
        byThreadKey: serializeDevicePersistedRightPanelState(state.byThreadKey),
      }),
      migrate: (persistedState) => {
        const migrated = migratePersistedRightPanelState(persistedState);
        return serializePersistedRightPanelStoreState(migrated);
      },
      merge: (persistedState, currentState) => {
        const restored = restoreDevicePersistedRightPanelStoreState(persistedState);
        return { ...currentState, ...restored };
      },
    },
  ),
);

export function selectThreadRightPanelState(
  byThreadKey: Record<string, ThreadRightPanelState>,
  ref: ScopedThreadRef | null | undefined,
): ThreadRightPanelState {
  if (!ref) return EMPTY_THREAD_STATE;
  return byThreadKey[scopedThreadKey(ref)] ?? EMPTY_THREAD_STATE;
}

export function selectActiveRightPanel(
  byThreadKey: Record<string, ThreadRightPanelState>,
  ref: ScopedThreadRef | null | undefined,
): RightPanelKind | null {
  const state = selectThreadRightPanelState(byThreadKey, ref);
  if (!state.isOpen) return null;
  return state.surfaces.find((surface) => surface.id === state.activeSurfaceId)?.kind ?? null;
}

export function selectActiveRightPanelSurface(
  byThreadKey: Record<string, ThreadRightPanelState>,
  ref: ScopedThreadRef | null | undefined,
): RightPanelSurface | null {
  const state = selectThreadRightPanelState(byThreadKey, ref);
  if (!state.isOpen) return null;
  return state.surfaces.find((surface) => surface.id === state.activeSurfaceId) ?? null;
}
